import {
	HistoryOutlined,
	LoginOutlined,
	LogoutOutlined,
} from "@mui/icons-material";
import {
	Box,
	CircularProgress,
	Drawer,
	Stack,
	Typography,
} from "@mui/material";
import { useEffect, useState } from "react";

import { getGeofenceVehicleEventsAsync } from "../../../services/Location";
import type { IGeofenceVehicleEventProps } from "../../../services/Location/types";

interface GeofenceHistoryPanelProps {
	open: boolean;
	onClose: () => void;
}

function formatEventDate(value: string) {
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return "-";

	return `${date.toLocaleDateString()} ${date.toLocaleTimeString([], {
		hour: "2-digit",
		minute: "2-digit",
	})}`;
}

export default function GeofenceHistoryPanel({
	open,
	onClose,
}: GeofenceHistoryPanelProps) {
	const [history, setHistory] = useState<IGeofenceVehicleEventProps[]>([]);
	const [isLoading, setIsLoading] = useState(false);

	useEffect(() => {
		if (!open) return;

		let isMounted = true;
		setIsLoading(true);

		getGeofenceVehicleEventsAsync({ page: 1, pageSize: 50 })
			.then((events) => {
				if (isMounted) setHistory(events);
			})
			.catch(() => {
				if (isMounted) setHistory([]);
			})
			.finally(() => {
				if (isMounted) setIsLoading(false);
			});

		return () => {
			isMounted = false;
		};
	}, [open]);

	return (
		<Drawer
			anchor="right"
			open={open}
			onClose={onClose}
			PaperProps={{
				sx: {
					width: { xs: "100vw", sm: 440 },
					maxWidth: "100vw",
					background: "linear-gradient(180deg, #0f172a 0%, #111827 100%)",
					color: "white",
				},
			}}
		>
			<Box sx={{ p: 3, borderBottom: "1px solid rgba(148,163,184,0.18)" }}>
				<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
					<HistoryOutlined sx={{ color: "#93c5fd" }} />
					<Typography variant="h6" sx={{ fontWeight: 800 }}>
						Histórico de cercas
					</Typography>
					{isLoading ? (
						<CircularProgress size={18} sx={{ ml: "auto", color: "#93c5fd" }} />
					) : null}
				</Box>
				<Typography variant="body2" sx={{ color: "#cbd5e1", mt: 0.5 }}>
					Entradas e saídas recentes de todos os veículos.
				</Typography>
			</Box>

			<Stack spacing={1} sx={{ p: 2, overflowY: "auto" }}>
				{history.length > 0 ? (
					history.map((event) => {
						const isEntry = event.eventType === "ENTER";
						return (
							<Box
								key={event.id}
								sx={{
									display: "flex",
									gap: 1.25,
									alignItems: "flex-start",
									p: 1.5,
									borderRadius: 2,
									backgroundColor: "var(--app-surface-header)",
									border: "1px solid var(--app-table-border)",
								}}
							>
								{isEntry ? (
									<LoginOutlined
										fontSize="small"
										sx={{ color: "#86efac", mt: 0.25 }}
									/>
								) : (
									<LogoutOutlined
										fontSize="small"
										sx={{ color: "#fca5a5", mt: 0.25 }}
									/>
								)}
								<Box sx={{ minWidth: 0 }}>
									<Typography variant="body2" sx={{ fontWeight: 800 }}>
										{event.vehiclePlate || event.vehicleId}
									</Typography>
									<Typography
										variant="caption"
										sx={{ color: "#cbd5e1", display: "block" }}
									>
										{isEntry ? "Entrou" : "Saiu"}
										{event.geofenceName ? ` - ${event.geofenceName}` : ""}
									</Typography>
									<Typography
										variant="caption"
										sx={{ color: "#94a3b8", display: "block" }}
									>
										{formatEventDate(event.occurredAt)}
									</Typography>
								</Box>
							</Box>
						);
					})
				) : (
					<Typography sx={{ color: "#94a3b8" }}>
						{isLoading
							? "Carregando histórico..."
							: "Nenhum evento de cerca registrado."}
					</Typography>
				)}
			</Stack>
		</Drawer>
	);
}
